import {
  Card,
  StatCard,
  StatusBadge,
  ProgressBar,
  Avatar,
  Ring,
  EmptyState,
} from "components/dashboard/ui";
import {
  Wallet,
  Users,
  ShieldCheck,
  ShoppingCart,
  AlertOctagon,
  Activity,
  TrendingUp,
  Truck,
  Pin,
  Coins,
  Bug,
  CheckSquare,
  Box,
  ChevronRight,
  Plus,
  Building2,
  X,
  Loader2,
  IndianRupee,
  FileText,
  CalendarDays,
  Tag,
  User,
  Folder,
  ClipboardCheck,
  Crown,
  Headphones,
  ArrowRight,
  Check,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { OwnerDashboardApi, SubscriptionAPI } from "api/dashboard/service";
import { PettyCashApi } from "api/petty/service";
import { useNotifications } from "utils/Notifications/NotificationContext";
import { formatCurrencyShort } from "utils/utilityFunctions";
import { useAlert } from "components/UI_components/Alert";
import MaterialRequest from "screens/Dashboard/Procurement/MaterialRequest";
import SubtaskDetailComponent from "screens/Project Dashboard/Tasks/SubTaskDetailComponent";

const FILTERS = ["Petty Cash", "Snags", "Tasks", "Procure"];
const APPROVAL_TYPE_STYLES = {
  pettycash: {
    iconBox: "border-amber-200 bg-amber-50",
    icon: "text-amber-600",
  },
  snags: {
    iconBox: "border-red-200 bg-red-50",
    icon: "text-red-600",
  },
  subtasks: {
    iconBox: "border-blue-200 bg-blue-50",
    icon: "text-blue-600",
  },
  procurement: {
    iconBox: "border-emerald-200 bg-emerald-50",
    icon: "text-emerald-600",
  },
};
const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];
const SUBSCRIPTION_FEATURES = [
  { icon: Folder, label: "Unlimited", sublabel: "Projects" },
  { icon: Users, label: "Team", sublabel: "Collaboration" },
  { icon: ClipboardCheck, label: "Attendance", sublabel: "Tracking" },
  { icon: Box, label: "Material &", sublabel: "Inventory" },
  { icon: FileText, label: "Payments &", sublabel: "Expenses" },
];

const safeNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const clampPercent = (value) =>
  Math.min(100, Math.max(0, Math.round(safeNumber(value))));

const getStoredOrganizationId = () => {
  try {
    return (
      JSON.parse(localStorage.getItem("organization") || "null")?.id || null
    );
  } catch (_) {
    return null;
  }
};

const hasActiveSubscription = (response) =>
  Boolean(
    response?.status &&
    response?.data?.active_flag &&
    response?.data?.valid_till > new Date().toISOString(),
  );

const monthLabel = (date) => date.toLocaleString("en-US", { month: "long" });
const shortMonthLabel = (month) => String(month || "").slice(0, 3);

const findFinanceMonth = (months, date) => {
  const month = monthLabel(date);
  const year = date.getFullYear();
  return (months || []).find(
    (item) => item?.month === month && safeNumber(item?.year) === year,
  );
};

const percentDiff = (current, previous) => {
  const currentValue = safeNumber(current);
  const previousValue = safeNumber(previous);
  if (!previousValue) return currentValue ? "+100%" : "0%";
  const diff = ((currentValue - previousValue) / previousValue) * 100;
  return `${diff >= 0 ? "+" : ""}${diff.toFixed(1).replace(".0", "")}%`;
};

const timeAgo = (dateLike) => {
  if (!dateLike) return "Just now";
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return "Just now";
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const initials = (first, last, fallback = "?") =>
  `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase() || fallback;

const initialsFromName = (name, fallback = "U") => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return initials(parts[0], parts.slice(1).join(" "), fallback);
};

const getApprovalDate = (item) =>
  item?.requested_on ||
  item?.created_on ||
  item?.last_updated_on ||
  item?.created ||
  item?.date;

const formatDateTime = (dateLike) => {
  if (!dateLike) return "—";
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getApprovalKey = (type, item = {}) => {
  const id =
    item.approval_id ||
    item.snag_id ||
    item.update_id ||
    item.material_request_id ||
    item.id;
  return `${type}:${id}`;
};

const normalizePettyCashApproval = (item = {}) => ({
  id: item.approval_id || item.id,
  amount: safeNumber(item.amount),
  category: item.category || "Petty cash approval",
  employeeName: item.requested_by.includes(" | ")
    ? item.requested_by.split(" | ")[1]
    : item.requested_by || "Requester",
  project: item.site_name || "Site",
  remarks: item.remarks || "",
  statusType: item.status || "PENDING",
  receipt: item.receipt,
  _raw: item,
});

const normalizeMaterialRequest = (item = {}) => ({
  ...item,
  id: item.material_request_id || item.id,
  code: item.code || item.mr_number,
  items: item.total_items ?? item.items ?? item.lines?.length ?? 0,
  raisedBy: item.raisedBy || item.created_by || "-",
  expectedBy: item.expectedBy || item.due_date || null,
  sites: item.sites || item.site_id,
  material_request_lines: Array.isArray(item.material_request_lines)
    ? item.material_request_lines
    : (item.lines || []).map((line) => ({
        ...line,
        id: line.id || line.line_id,
        material: line.material || line.material_id,
        material_name: line.material_name,
        quantity: line.quantity,
        status: line.status,
      })),
});

const toAbsoluteAssetUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  if (/^(https?:|blob:|data:)/i.test(url)) return url;
  const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
  if (!baseUrl) return url;
  return url.startsWith("/") ? `${baseUrl}${url}` : `${baseUrl}/${url}`;
};

const isImageAsset = (url = "") =>
  /\.(png|jpe?g|gif|webp|bmp|svg)($|\?)/i.test(String(url));

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg border border-border bg-sm-card p-3">
    <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {Icon && <Icon className="size-3.5" />}
      {label}
    </div>
    <div className="text-sm font-semibold text-foreground">{value || "—"}</div>
  </div>
);

function PettyCashApprovalDrawer({
  approval,
  organizationId,
  onClose,
  onActionComplete,
}) {
  const { addAlert } = useAlert();
  const normalized = useMemo(
    () => normalizePettyCashApproval(approval),
    [approval],
  );
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");

  useEffect(() => {
    if (!organizationId || !normalized.id) return;

    let cancelled = false;
    setLoading(true);
    PettyCashApi.getApprovalDetail(organizationId, normalized.id)
      .then((response) => {
        if (!cancelled) setDetail(response?.data || null);
      })
      .catch((error) => {
        console.error("Failed to fetch petty cash approval detail", error);
        if (!cancelled) {
          addAlert?.("Failed to load petty cash approval detail", "error");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId, normalized.id, addAlert]);

  const transactionDetails =
    detail?.transaction_details ||
    detail?.transaction_payload ||
    approval?.transaction_payload ||
    {};
  const receiptUrl = toAbsoluteAssetUrl(
    detail?.receipt ||
      detail?.attachment ||
      transactionDetails?.receipt ||
      transactionDetails?.attachment ||
      approval?.receipt ||
      approval?.attachment,
  );

  const handleAction = async (status) => {
    if (!organizationId || !normalized.id || actionLoading) return;
    setActionLoading(status);
    try {
      await PettyCashApi.actionApproval(
        organizationId,
        normalized.id,
        status,
        "",
      );
      addAlert?.(
        `Petty cash approval ${status === "APPROVED" ? "approved" : "rejected"} successfully`,
        "success",
      );
      onActionComplete?.(normalized.id, status);
      onClose();
    } catch (error) {
      console.error("Failed to action petty cash approval", error);
      addAlert?.("Failed to update petty cash approval", "error");
    } finally {
      setActionLoading("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative flex h-full w-full flex-col border-l border-border bg-white shadow-xl md:w-[560px]">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-foreground">
              Petty Cash Approval
            </h2>
            {/* <p className="text-xs text-muted-foreground">
              Reference ID: #{normalized.id}
            </p> */}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close petty cash approval"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pt-3 pb-36 ">
          {loading ? (
            <div className="flex h-48 flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
              <Loader2 className="size-6 animate-spin text-sm-primary" />
              Loading approval details...
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Stat
                  icon={IndianRupee}
                  label="Amount"
                  value={`₹${normalized.amount.toLocaleString("en-IN")}`}
                />
                <Stat icon={Tag} label="Category" value={normalized.category} />
                <Stat
                  icon={User}
                  label="Requested By"
                  value={normalized.employeeName}
                />
                <Stat
                  icon={Pin}
                  label="Project / Site"
                  value={normalized.project}
                />
                <Stat
                  icon={CalendarDays}
                  label="Requested On"
                  value={formatDateTime(approval?.requested_on)}
                />
                <Stat
                  icon={ShieldCheck}
                  label="Status"
                  value={normalized.statusType}
                />
              </div>

              {normalized.remarks && (
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
                    <FileText className="size-4" />
                    Remarks
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {normalized.remarks}
                  </p>
                </div>
              )}

              {receiptUrl && (
                <div className="rounded-lg border border-border bg-sm-card p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                      <FileText className="size-4" />
                      Receipt
                    </div>
                    <a
                      href={receiptUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-semibold text-sm-primary hover:underline"
                    >
                      Open
                    </a>
                  </div>
                  {isImageAsset(receiptUrl) ? (
                    <img
                      src={receiptUrl}
                      alt="Petty cash receipt"
                      className="max-h-72 w-full rounded-lg border border-border object-contain"
                    />
                  ) : (
                    <div className="rounded-lg bg-muted/40 p-3 text-sm text-muted-foreground">
                      Receipt file is available to open in a new tab.
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-3">
                <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  Transaction Details
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {Object.entries(transactionDetails).map(([key, value]) => {
                    if (
                      value === null ||
                      value === undefined ||
                      typeof value === "object"
                    ) {
                      return null;
                    }
                    return (
                      <div
                        key={key}
                        className="rounded-lg border border-border p-3"
                      >
                        <div className="text-[11px] capitalize text-muted-foreground">
                          {key.replace(/_/g, " ")}
                        </div>
                        <div className="mt-1 break-words text-sm font-medium text-foreground">
                          {String(value)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="absolute inset-x-0 bottom-0 flex gap-3 border-t border-border bg-white px-5 py-4">
          <button
            type="button"
            onClick={() => handleAction("REJECTED")}
            disabled={Boolean(actionLoading)}
            className="flex-1 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 disabled:opacity-60"
          >
            {actionLoading === "REJECTED" ? "Rejecting..." : "Reject"}
          </button>
          <button
            type="button"
            onClick={() => handleAction("APPROVED")}
            disabled={Boolean(actionLoading)}
            className="flex-1 rounded-lg bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
          >
            {actionLoading === "APPROVED" ? "Approving..." : "Approve"}
          </button>
        </div>
      </div>
    </div>
  );
}

const getLineSummary = (lines = []) => {
  const names = lines.map((line) => line?.material_name).filter(Boolean);
  if (!names.length) return "Material request";
  if (names.length === 1) return names[0];
  return `${names.slice(0, 2).join(", ")}${names.length > 2 ? ` +${names.length - 2}` : ""}`;
};

const getRecordLabel = (recordType) => {
  const type = String(recordType || "").toLowerCase();
  if (type.includes("purchase")) return "PO";
  if (type.includes("rfq")) return "RFQ";
  if (type.includes("material")) return "MR";
  return "REQ";
};

const getBudgetTone = (percentage) => {
  if (percentage >= 90) return "alert";
  if (percentage >= 75) return "warn";
  if (percentage <= 0) return "neutral";
  return "good";
};

const getBudgetStatus = (percentage, hasBudget) => {
  if (!hasBudget) return "No budget";
  if (percentage >= 100) return "Over";
  if (percentage >= 75) return "Watch";
  return "On track";
};

const activityTone = (notification) => {
  const module = String(
    notification?.module || notification?.type || "",
  ).toLowerCase();
  if (module.includes("delivery")) return "emerald";
  if (
    module.includes("po") ||
    module.includes("procurement") ||
    module.includes("material")
  )
    return "amber";
  if (module.includes("design")) return "violet";
  if (module.includes("snag")) return "red";
  return "blue";
};

function GlobalOwner({ hasNoOrganization = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [filter, setFilter] = useState("All");
  const [organizationId, setOrganizationId] = useState(getStoredOrganizationId);
  const [subscriptionActive, setSubscriptionActive] = useState(null);
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [approvals, setApprovals] = useState(null);
  const [activeApproval, setActiveApproval] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { notifications = [] } = useNotifications();

  useEffect(() => {
    const syncOrganization = () => setOrganizationId(getStoredOrganizationId());
    window.addEventListener("storage", syncOrganization);
    window.addEventListener("organizationChanged", syncOrganization);
    return () => {
      window.removeEventListener("storage", syncOrganization);
      window.removeEventListener("organizationChanged", syncOrganization);
    };
  }, []);

  useEffect(() => {
    if (!organizationId) {
      setSubscriptionActive(null);
      return;
    }

    let cancelled = false;

    SubscriptionAPI.checkUserSubscription(organizationId)
      .then((response) => {
        if (!cancelled) {
          setSubscriptionActive(hasActiveSubscription(response));
        }
      })
      .catch((subscriptionError) => {
        if (cancelled) return;
        console.error("Error checking owner subscription:", subscriptionError);
        setSubscriptionActive(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("focusApprovalInbox") !== "true") return;

    window.requestAnimationFrame(() => {
      document
        .getElementById("approval-inbox")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [location.search]);

  useEffect(() => {
    if (!organizationId) {
      setSummary(null);
      setProjects([]);
      setApprovals(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    Promise.allSettled([
      OwnerDashboardApi.getSummary(organizationId),
      OwnerDashboardApi.getProjectSummaries(organizationId),
      OwnerDashboardApi.getAllApprovals(organizationId),
    ])
      .then(([summaryResult, projectsResult, approvalsResult]) => {
        if (cancelled) return;
        const failures = [
          summaryResult,
          projectsResult,
          approvalsResult,
        ].filter((result) => result.status === "rejected");
        const summaryResponse =
          summaryResult.status === "fulfilled" ? summaryResult.value : null;
        const projectsResponse =
          projectsResult.status === "fulfilled" ? projectsResult.value : null;
        const approvalsResponse =
          approvalsResult.status === "fulfilled" ? approvalsResult.value : null;

        setSummary(summaryResponse?.data || null);
        setProjects(
          Array.isArray(projectsResponse?.data) ? projectsResponse.data : [],
        );
        setApprovals(approvalsResponse?.data || null);
        if (failures.length) {
          console.error(
            "Error fetching owner dashboard:",
            failures.map((failure) => failure.reason),
          );
          setError("Some owner dashboard data could not be loaded.");
        }
      })
      .catch((fetchError) => {
        if (cancelled) return;
        console.error("Error fetching owner dashboard:", fetchError);
        setError(fetchError?.message || "Could not load owner dashboard.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const taskOverview = summary?.task_overview || EMPTY_OBJECT;
  const workforceOverview = summary?.workforce_overview || EMPTY_OBJECT;
  const procurementOverview = summary?.procurement_overview || EMPTY_OBJECT;
  const financeOverview = summary?.finance_overview || EMPTY_OBJECT;
  const executiveSnapshot = summary?.executive_snapshot || EMPTY_OBJECT;
  const sixMonthsFinance = summary?.six_months_finance || EMPTY_ARRAY;

  const now = useMemo(() => new Date(), []);
  const lastMonthDate = useMemo(
    () => new Date(now.getFullYear(), now.getMonth() - 1, 1),
    [now],
  );
  const currentMonthFinance =
    findFinanceMonth(sixMonthsFinance, now) || sixMonthsFinance[0] || {};
  const previousMonthFinance =
    findFinanceMonth(sixMonthsFinance, lastMonthDate) ||
    sixMonthsFinance[1] ||
    {};
  const spendMtd = safeNumber(currentMonthFinance?.expenses);
  const spendTrend = percentDiff(spendMtd, previousMonthFinance?.expenses);
  const totalWorkers =
    safeNumber(workforceOverview?.total_workers) +
    safeNumber(workforceOverview?.total_contractors);
  const presentWorkers =
    safeNumber(workforceOverview?.workers_present) +
    safeNumber(workforceOverview?.contractors_present);
  const attendance = totalWorkers
    ? clampPercent((presentWorkers / totalWorkers) * 100)
    : 0;

  const cashFlow = useMemo(
    () =>
      [...sixMonthsFinance].reverse().map((item) => ({
        m: shortMonthLabel(item?.month),
        inflow: safeNumber(item?.received),
        outflow: safeNumber(item?.expenses),
      })),
    [sixMonthsFinance],
  );

  const projectFinance = useMemo(
    () =>
      projects.slice(0, 4).map((project) => {
        const budget = safeNumber(project?.estimated_budget);
        const expenses = safeNumber(project?.total_expenses);
        const spent = budget ? clampPercent((expenses / budget) * 100) : 0;
        return {
          name: project?.site_name || "Unnamed project",
          budget: budget ? formatCurrencyShort(budget) : "Budget N/A",
          spent,
          status: getBudgetStatus(spent, Boolean(budget)),
          tone: getBudgetTone(spent),
        };
      }),
    [projects],
  );

  const inbox = useMemo(() => {
    const data = approvals || {};
    const pettycash = (data.pettycash || []).map((item) => ({
      key: getApprovalKey("pettycash", item),
      typeKey: "pettycash",
      type: "Petty Cash",
      icon: Coins,
      iconStyle: APPROVAL_TYPE_STYLES.pettycash,
      who: item?.requested_by || "Requester",
      desc: item?.remarks || item?.category || "Petty cash approval",
      amt: formatCurrencyShort(safeNumber(item?.amount)),
      time: timeAgo(getApprovalDate(item)),
      rawDate: getApprovalDate(item),
      raw: item,
    }));
    const snags = (data.snags || []).map((item) => ({
      key: getApprovalKey("snags", item),
      typeKey: "snags",
      type: "Snags",
      icon: Bug,
      iconStyle: APPROVAL_TYPE_STYLES.snags,
      who: item?.created_by || "Requester",
      desc: item?.title || item?.category || "Snag approval",
      amt: item?.site_name || "Site",
      time: timeAgo(getApprovalDate(item)),
      rawDate: getApprovalDate(item),
      raw: item,
    }));
    const subtasks = (data.subtasks || []).map((item) => ({
      key: getApprovalKey("subtasks", item),
      typeKey: "subtasks",
      type: "Tasks",
      icon: CheckSquare,
      iconStyle: APPROVAL_TYPE_STYLES.subtasks,
      who: item?.created_by || "Requester",
      desc: item?.subtask_name || item?.task_name || "Subtask update",
      amt: item?.site_name || "Site",
      time: timeAgo(getApprovalDate(item)),
      rawDate: getApprovalDate(item),
      raw: item,
    }));
    const procurement = (data.procurement || []).map((item) => ({
      key: getApprovalKey("procurement", item),
      typeKey: "procurement",
      type: "Procure",
      icon: Box,
      iconStyle: APPROVAL_TYPE_STYLES.procurement,
      who: item?.created_by || "Requester",
      desc: `${item?.mr_number || "Material request"} · ${safeNumber(item?.total_items)} items`,
      amt: item?.site_name || "Site",
      time: timeAgo(getApprovalDate(item)),
      rawDate: getApprovalDate(item),
      raw: item,
    }));

    return [...pettycash, ...snags, ...subtasks, ...procurement].sort(
      (a, b) => new Date(b.rawDate || 0) - new Date(a.rawDate || 0),
    );
  }, [approvals]);

  const approvalFilterCounts = useMemo(() => {
    const counts = FILTERS.reduce(
      (acc, type) => ({
        ...acc,
        [type]: 0,
      }),
      { All: inbox.length },
    );

    inbox.forEach((item) => {
      counts[item.type] = safeNumber(counts[item.type]) + 1;
    });

    return counts;
  }, [inbox]);

  const filtered = inbox.filter((i) => filter === "All" || i.type === filter);
  const pendingApprovalCount = safeNumber(
    approvals?.summary?.total,
    safeNumber(executiveSnapshot?.pending_approvals),
  );

  const removeApprovalFromInbox = (typeKey, rawItem) => {
    const itemKey = getApprovalKey(typeKey, rawItem);
    setApprovals((prev) => {
      if (!prev) return prev;
      const nextList = (prev[typeKey] || []).filter(
        (item) => getApprovalKey(typeKey, item) !== itemKey,
      );
      return {
        ...prev,
        summary: {
          ...(prev.summary || {}),
          total: Math.max(0, safeNumber(prev.summary?.total) - 1),
          [typeKey]: Math.max(0, safeNumber(prev.summary?.[typeKey]) - 1),
        },
        [typeKey]: nextList,
      };
    });
  };

  const handleTakeAction = (item) => {
    if (!item) return;

    if (item.typeKey === "snags") {
      const snagId = item.raw?.snag_id || item.raw?.id;
      if (snagId) {
        navigate(
          `/dashboard/snags?snagId=${encodeURIComponent(snagId)}&fromApprovalInbox=true`,
        );
      }
      return;
    }

    setActiveApproval(item);
  };

  const handlePettyCashActionComplete = (approvalId) => {
    removeApprovalFromInbox("pettycash", { approval_id: approvalId });
  };

  const handleMaterialRequestClosed = () => {
    setActiveApproval(null);
  };

  const handleMaterialRequestAction = (rawItem) => {
    removeApprovalFromInbox("procurement", rawItem);
    setActiveApproval(null);
  };

  const handleDesignPinClick = (designId) => {
    if (!designId) return;
    navigate(`/dashboard/design?designId=${encodeURIComponent(designId)}`);
  };

  const handleVendorClick = (vendorId) => {
    if (!vendorId) return;
    navigate(`/dashboard/vendor?vendorId=${encodeURIComponent(vendorId)}`);
  };

  const teamMembers = useMemo(
    () =>
      (summary?.team_overview || []).map((member) => {
        const load = clampPercent(member?.load);
        const name =
          `${member?.first_name || ""} ${member?.last_name || ""}`.trim() ||
          "Team member";
        return {
          name,
          user_id: member?.user_id,
          userpic: member?.userpic,
          role: `${member?.profession || "Member"} · ${safeNumber(member?.projects_assigned)} projects`,
          load,
          eff: clampPercent(
            100 -
              safeNumber(
                member?.total_snags_assigned,
              ),
          ),
          init: initials(member?.first_name, member?.last_name, "TM"),
        };
      }),
    [summary],
  );
  const teamWorkload = useMemo(() => teamMembers.slice(0, 5), [teamMembers]);
  const teamMemberByUserId = useMemo(() => {
    const members = new Map();
    teamMembers.forEach((member) => {
      if (member.user_id !== null && member.user_id !== undefined) {
        members.set(String(member.user_id), member);
      }
    });
    return members;
  }, [teamMembers]);

  const procureTrend = useMemo(() => {
    const values = procurementOverview?.last_six_month_po || {};
    return Object.entries(values)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, value]) => ({
        m: new Date(`${month}-01T00:00:00`).toLocaleString("en-US", {
          month: "short",
        }),
        v: safeNumber(value),
      }));
  }, [procurementOverview]);

  const currentPoMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const previousPoMonthKey = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, "0")}`;
  const currentPoSpend = safeNumber(
    procurementOverview?.last_six_month_po?.[currentPoMonthKey],
  );
  const previousPoSpend = safeNumber(
    procurementOverview?.last_six_month_po?.[previousPoMonthKey],
  );
  const topVendor = summary?.vendors_overview?.[0];

  const recentProcurement = useMemo(
    () =>
      (procurementOverview?.recent_requests || []).slice(0, 5).map((item) => {
        const label = getRecordLabel(item?.record_type);
        const number =
          item?.po_number ||
          item?.rfq_number ||
          item?.mr_number ||
          `${label}-${item?.id}`;
        const total = (item?.lines || []).reduce(
          (sum, line) => sum + safeNumber(line?.total_price),
          0,
        );
        return {
          type: label,
          item: getLineSummary(item?.lines),
          meta: `${number} · ${item?.site_name || item?.vendor_name || "Project"} · ${timeAgo(item?.created_on)} ago`,
          amount: total
            ? formatCurrencyShort(total)
            : `${(item?.lines || []).length} items`,
        };
      }),
    [procurementOverview],
  );

  const recentDeliveries = useMemo(
    () =>
      (summary?.recent_deliveries || []).slice(0, 5).map((delivery) => ({
        delivery_id: delivery.delivery_id,
        material: `${delivery?.item_name || "Material"} · ${safeNumber(delivery?.quantity)} qty`,
        meta: `${delivery?.vendor_name || "Vendor"} · ${delivery?.site_name || "Site"} · ${timeAgo(delivery?.last_updated_on)} ago`,
        status: delivery?.status || "Upcoming",
        tone:
          String(delivery?.status || "").toLowerCase() === "delivered"
            ? "good"
            : "warn",
      })),
    [summary],
  );

  const recentPins = useMemo(
    () =>
      (summary?.recent_design_pins || []).slice(0, 5).map((pin) => ({
        title: pin?.comments || pin?.design_name || "Design pin",
        design_id: pin?.design_id,
        design_pin_id: pin?.design_pin_id,
        meta: `${pin?.site_name || "Site"} · by ${pin?.created_by || "Member"} · ${timeAgo(pin?.created_on)}`,
      })),
    [summary],
  );

  const vendorsOverview = useMemo(
    () =>
      (summary?.vendors_overview || []).slice(0, 5).map((vendor) => {
        const purchaseValue = safeNumber(vendor?.total_purchase_value);
        const paid = safeNumber(vendor?.total_paid);
        const due = Math.max(0, purchaseValue - paid);
        return {
          name: vendor?.vendor_name || "Vendor",
          vendor_id: vendor?.vendor_id,
          init: initials(vendor?.vendor_name, "", "V"),
          meta: `${safeNumber(vendor?.number_of_pos)} POs · spend ${formatCurrencyShort(purchaseValue)}`,
          due: due ? `Due ${formatCurrencyShort(due)}` : "Settled",
          tone: due ? "alert" : "good",
          age: `Paid ${formatCurrencyShort(paid)}`,
        };
      }),
    [summary],
  );

  const liveActivities = useMemo(
    () =>
      notifications.slice(0, 5).map((notification) => {
        const actorId =
          notification?.actor_id ||
          notification?.metadata?.actor_id ||
          notification?.actor?.id;
        const actor = teamMemberByUserId.get(String(actorId));
        const actorName =
          notification?.actor_name ||
          actor?.name ||
          notification?.actor?.name ||
          "SiteMaster";

        return {
          text: notification?.title || notification?.message || "New activity",
          actorName,
          actorUserpic: actor?.userpic || notification?.actor_userpic || "",
          actorInitials: actor?.init || initialsFromName(actorName, "SM"),
          site:
            notification?.site_name ||
            notification?.metadata?.site_name ||
            notification?.module ||
            "Activity",
          time: timeAgo(notification?.date),
          tone: activityTone(notification),
        };
      }),
    [notifications, teamMemberByUserId],
  );

  const activeProjects = safeNumber(
    executiveSnapshot?.active_projects,
    projects.filter((project) => project?.active_flag).length,
  );
  const delayedTasks = safeNumber(taskOverview?.delayed_tasks);
  const delayedSub = taskOverview?.most_delayed_site?.site_name
    ? `${taskOverview.most_delayed_site.site_name} · ${safeNumber(taskOverview.most_delayed_site.count)} delayed`
    : "across projects";
  const inflow = safeNumber(financeOverview?.received_expenses);
  const outflow = safeNumber(financeOverview?.total_expenses);
  const net = inflow - outflow;

  if (hasNoOrganization || !organizationId) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center px-6 py-10">
        <div className="w-full max-w-md rounded-lg border border-border bg-sm-card p-8 text-center shadow-sm">
          <div className="mx-auto mb-5 flex size-14 items-center justify-center rounded-full bg-purple-100 text-purple-700">
            <Building2 className="size-7" />
          </div>
          <h2 className="mb-6 text-2xl font-semibold text-foreground">
            Your Organization/ Company Details
          </h2>

          <button
            type="button"
            onClick={() => navigate("/organization-details")}
            className="mb-6 flex w-full items-center justify-center rounded-lg bg-purple-100 px-4 py-3 font-semibold text-purple-700 transition-colors hover:bg-purple-200"
          >
            <span>Add Your Organization</span>
            <Plus className="ml-2 size-5" />
          </button>

          <p className="text-center text-gray-600">
            Create an organization to add company members
          </p>
        </div>
      </div>
    );
  }

  if (organizationId && subscriptionActive === false) {
    return (
      <div className="flex min-h-[65vh] flex-1 items-center justify-center bg-[#f8f8fb] p-3 sm:px-8">
        <div className="w-full max-w-4xl overflow-hidden rounded-lg border border-[#dedee8] bg-white text-center shadow-[0_18px_45px_rgba(15,23,42,0.12)]">
          <div className="px-3 pb-3 pt-4 sm:px-14 sm:pb-6">
            <div
              className="relative mx-auto mb-3 flex h-32 max-w-xl items-center justify-center"
              aria-hidden="true"
            >
              <span className="absolute left-[14%] top-[31%] text-3xl font-bold leading-none text-[#7a35d7]">
                +
              </span>
              <span className="absolute left-[26%] top-[62%] text-3xl font-bold leading-none text-[#d9d8e4]">
                +
              </span>
              <span className="absolute right-[23%] top-[26%] text-3xl font-bold leading-none text-[#7a35d7]">
                +
              </span>
              <span className="absolute right-[12%] top-[51%] text-3xl font-bold leading-none text-[#d9d8e4]">
                +
              </span>

              <div className="relative flex size-44 scale-[0.78] items-center justify-center rounded-full bg-[#f0e8ff]">
                <div className="absolute left-8 top-14 h-4 w-11 rounded-t-full bg-white/90" />
                <div className="absolute right-8 top-7 h-4 w-12 rounded-t-full bg-white/90" />
                <div className="absolute right-7 top-16 h-20 w-16 text-[#8067e0]">
                  <div className="absolute left-1 top-0 h-px w-12 rotate-[-18deg] bg-current" />
                  <div className="absolute left-1 top-0 h-px w-12 rotate-[18deg] bg-current" />
                  <div className="absolute left-5 top-0 h-20 w-px bg-current" />
                  <div className="absolute left-0 top-5 h-px w-16 bg-current" />
                  <div className="absolute right-0 top-5 h-8 w-px bg-current" />
                  <div className="absolute right-0 top-12 size-3 bg-current" />
                </div>
                <div className="absolute bottom-8 left-8 h-14 w-12 rounded-t-md bg-[#6f2ce0]" />
                <div className="absolute bottom-8 left-[74px] h-24 w-16 rounded-t-md bg-[#4a14bd]" />
                <div className="absolute bottom-8 right-[72px] h-14 w-12 rounded-t-md bg-[#6f2ce0]" />
                {[0, 1, 2].map((row) =>
                  [0, 1].map((col) => (
                    <span
                      key={`${row}-${col}`}
                      className="absolute size-3 rounded-sm bg-white"
                      style={{
                        left: `${88 + col * 24}px`,
                        top: `${58 + row * 30}px`,
                      }}
                    />
                  )),
                )}
                <span className="absolute bottom-16 left-[50px] h-2 w-4 rounded-sm bg-white" />
                <span className="absolute bottom-10 left-[50px] h-2 w-4 rounded-sm bg-white" />
                <div className="absolute bottom-7 right-11 flex size-14 items-center justify-center rounded-full bg-[#3b0069] text-white shadow-lg">
                  <Check className="size-8 stroke-[3]" />
                </div>
              </div>
            </div>

            <h2 className="text-xl font-extrabold tracking-tight text-[#111827] sm:text-3xl">
              Welcome to SiteMaster 👋
            </h2>
            <p className="mt-2 text-base font-bold text-[#686b78]">
              Your organization setup is almost complete.
            </p>
            <p className="mx-auto mt-3 max-w-3xl text-base font-medium leading-7 text-[#3f4350]">
              To access SiteMaster features and start managing your projects,
              activate a subscription plan.
            </p>

            <div className="mx-auto mt-6 grid max-w-4xl grid-cols-2 gap-y-5 sm:grid-cols-3 lg:grid-cols-5 lg:divide-x lg:divide-[#e4e5ec]">
              {SUBSCRIPTION_FEATURES.map(
                ({ icon: Icon, label, sublabel }, index) => (
                  <div
                    key={`${label}-${sublabel}`}
                    className={`px-4 ${
                      index === SUBSCRIPTION_FEATURES.length - 1
                        ? "col-span-2 sm:col-span-1"
                        : ""
                    }`}
                  >
                    <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-lg bg-[#f1e8fb] text-[#6d22c8]">
                      <Icon className="size-7 stroke-[1.8]" />
                    </div>
                    <div className="text-sm font-extrabold leading-6 text-[#111827]">
                      <div className="whitespace-nowrap">{label}</div>
                      <div className="whitespace-nowrap">{sublabel}</div>
                    </div>
                  </div>
                ),
              )}
            </div>

            <div className="mt-6 border-t border-[#eef0f5] pt-5">
              <button
                type="button"
                onClick={() => navigate("/subscription")}
                className="inline-flex min-h-14 w-[90%] flex-wrap items-center justify-center gap-3 rounded-lg bg-sm-primary px-6 text-lg font-extrabold text-white shadow-[0_10px_24px_rgba(59,0,105,0.24)] transition-colors hover:bg-[#3b0069] focus:outline-none focus:ring-4 focus:ring-[#cbb8ff] sm:gap-6"
              >
                <Crown className="size-8 stroke-[1.8]" />
                <span className="text-center leading-tight">
                  Choose Subscription Plan
                </span>
                <ArrowRight className="size-9 stroke-[2.2]" />
              </button>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center gap-3 border-t border-[#e4e5ec] bg-white px-5 py-3 text-sm sm:flex-row">
            <span className="flex size-8 items-center justify-center rounded-full bg-[#f1eef8] text-[#4d08c7]">
              <Headphones className="size-4" />
            </span>
            <span className="font-medium text-[#626676]">
              Need help selecting a plan?
            </span>
            <button
              type="button"
              onClick={() => navigate("/dashboard/help?tab=raise")}
              className="inline-flex items-center gap-3 font-extrabold text-[#4d08c7] transition-colors hover:text-[#3b0069] focus:outline-none focus:ring-2 focus:ring-[#cbb8ff]"
            >
              <span>Contact our team</span>
              <ArrowRight className="size-7" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 px-10 pt-8 pb-4">
      {/* <PageHeader title="Command Center" eyebrow="Global Dashboard">
        <button className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium border border-border bg-card hover:bg-muted">
          <Download className="size-4" />Export
        </button>
        <button className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium border border-border bg-card hover:bg-muted">
          <Mail className="size-4" />Weekly Digest
        </button>
      </PageHeader> */}

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {error}
        </div>
      )}
      {loading && !summary && (
        <div className="mb-4 rounded-lg border border-border bg-sm-card px-4 py-3 text-sm text-muted-foreground">
          Loading owner dashboard...
        </div>
      )}

      {/* TOP KPI ROW — 6 narrow */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <StatCard
          label="Active Projects"
          value={activeProjects}
          sub={
            <span className="text-sm-primary">
              {safeNumber(projects.length)} total projects
            </span>
          }
          tone="neutral"
        />
        <StatCard
          label="Spend MTD"
          value={formatCurrencyShort(spendMtd)}
          sub="vs last month"
          tone={
            spendMtd > safeNumber(previousMonthFinance?.expenses)
              ? "alert"
              : "good"
          }
          trend={spendTrend}
          trendUp={spendMtd <= safeNumber(previousMonthFinance?.expenses)}
        />
        <StatCard
          label="Delayed Tasks"
          value={delayedTasks}
          sub={delayedSub}
          tone={delayedTasks ? "warn" : "good"}
        />
        <StatCard
          label="Attendance"
          value={`${attendance}%`}
          sub={`${presentWorkers}/${totalWorkers} present`}
          tone={attendance >= 80 ? "good" : attendance >= 50 ? "warn" : "alert"}
        >
          <div className="absolute top-3 right-3">
            <Ring
              value={attendance}
              size={36}
              stroke={4}
              color="var(--color-emerald-500, #10b981)"
              track="var(--muted)"
            />
          </div>
        </StatCard>
        <StatCard
          label="Approvals Pending"
          value={pendingApprovalCount}
          sub={<span className="text-amber-600">awaiting you</span>}
          tone={pendingApprovalCount ? "warn" : "good"}
        />
        <StatCard
          label="Avg Procure Cycle"
          value={`${safeNumber(executiveSnapshot?.procurement_cycle ?? procurementOverview?.avg_procurement_cycle).toFixed(1)}d`}
          sub="avg cycle"
          tone="neutral"
        />
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* FINANCE CONTROL CENTER */}
        <Card
          className="col-span-12 xl:col-span-8"
          title="Finance Control Center"
          icon={<Wallet className="size-3.5" />}
          // action={
          //   <div className="flex gap-1 p-1 rounded-lg bg-muted text-[11px] font-bold uppercase">
          //     <span className="px-2.5 py-1 rounded bg-card text-foreground shadow-sm">Cash Flow</span>
          //     <span className="px-2.5 py-1 text-muted-foreground">Burn</span>
          //     <span className="px-2.5 py-1 text-muted-foreground">Margin</span>
          //   </div>
          // }
        >
          <div className="grid grid-cols-3 gap-4 mb-5">
            <div className="rounded-xl border border-border p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                Inflow
              </div>
              <div className="text-2xl font-bold text-emerald-600 mt-1 font-mono">
                {formatCurrencyShort(inflow)}
              </div>
            </div>
            <div className="rounded-xl border border-border p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                Outflow
              </div>
              <div className="text-2xl font-bold text-red-500 mt-1 font-mono">
                {formatCurrencyShort(outflow)}
              </div>
            </div>
            <div className="rounded-xl border border-border p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                Net
              </div>
              <div
                className={`text-2xl font-bold mt-1 font-mono ${net >= 0 ? "text-emerald-600" : "text-red-500"}`}
              >
                {net >= 0 ? "+" : ""}
                {formatCurrencyShort(net)}
              </div>
            </div>
          </div>
          <div className="h-[160px]">
            {cashFlow.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={cashFlow}
                  margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                >
                  <defs>
                    <linearGradient id="inG" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
                      <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="outG" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#3B0069" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#3B0069" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="2 4"
                    stroke="var(--border)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="m"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                  />
                  <YAxis hide />
                  <Tooltip
                    contentStyle={{
                      background: "#ffffff",
                      border: "1px solid #C3C3C3",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Area
                    dataKey="inflow"
                    stroke="#10b981"
                    strokeWidth={2}
                    fill="url(#inG)"
                  />
                  <Area
                    dataKey="outflow"
                    stroke="#3B0069"
                    strokeWidth={2}
                    fill="url(#outG)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState
                icon={<Wallet className="size-5" />}
                label="No finance trend yet"
              />
            )}
          </div>
          <div className="flex gap-4 mt-2 text-[11px] text-muted-foreground font-medium">
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-emerald-500" />
              Inflow
            </span>
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-sm-primary" />
              Outflow
            </span>
          </div>
        </Card>

        {/* PROJECT FINANCE */}
        <Card
          className="col-span-12 xl:col-span-4"
          title="Project-wise Finance"
          icon={<TrendingUp className="size-3.5" />}
          action={
            <button
              type="button"
              onClick={() => navigate("/dashboard/projects")}
              className="text-xs text-sm-primary font-semibold cursor-pointer hover:underline"
            >
              All projects →
            </button>
          }
        >
          <ul className="space-y-4">
            {projectFinance.map((p) => (
              <li key={p.name}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-sm font-semibold">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">
                      {p.budget}
                    </div>
                  </div>
                  <StatusBadge tone={p.tone}>{p.status}</StatusBadge>
                </div>
                <ProgressBar value={p.spent} showLabel />
              </li>
            ))}
          </ul>
          {!projectFinance.length && (
            <EmptyState
              icon={<TrendingUp className="size-5" />}
              label="No project finance data"
            />
          )}
        </Card>

        {/* APPROVAL INBOX */}
        <div
          id="approval-inbox"
          className="col-span-12 xl:col-span-7 scroll-mt-4"
        >
          <Card
            className="h-full"
            title="Approval Inbox"
            icon={<ShieldCheck className="size-3.5" />}
            action={
              <StatusBadge tone={pendingApprovalCount ? "warn" : "good"}>
                {inbox.length} Pending
              </StatusBadge>
            }
          >
            <div className="flex gap-1.5 mb-4 flex-wrap">
              {["All", ...FILTERS].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${filter === f ? "bg-sm-primary text-white" : "bg-sm-muted text-muted-foreground hover:bg-muted/70"}`}
                >
                  {f} ({approvalFilterCounts[f] || 0})
                </button>
              ))}
            </div>
            <div className="space-y-2">
              {filtered.map((it) => {
                return (
                  <div
                    key={it.key}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border bg-sm-card"
                  >
                    <div
                      className={`size-9 rounded-lg border flex items-center justify-center shrink-0 ${it.iconStyle?.iconBox || "border-border bg-muted"}`}
                    >
                      <it.icon
                        className={`size-4 ${it.iconStyle?.icon || "text-muted-foreground"}`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {it.desc}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {it.who} · <span className="font-mono">{it.amt}</span> ·{" "}
                        {it.time} ago
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleTakeAction(it)}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-sm-primary px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-sm-primary/90"
                    >
                      Take Action
                      <ChevronRight className="size-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
            {!filtered.length && (
              <EmptyState
                icon={<ShieldCheck className="size-5" />}
                label="No approvals in this view"
              />
            )}
          </Card>
        </div>

        {/* TEAM WORKLOAD */}
        <Card
          className="col-span-12 xl:col-span-5"
          title="Team Workload"
          icon={<Users className="size-3.5" />}
          action={
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
              <AlertOctagon className="size-3.5 text-amber-500" />
              {teamWorkload.length} active ·{" "}
              {teamWorkload.filter((member) => member.load >= 90).length}{" "}
              overloaded
            </span>
          }
        >
          <div className="space-y-1">
            {teamWorkload.map((m) => {
              const loadTone =
                m.load >= 90 ? "alert" : m.load >= 70 ? "warn" : "good";
              const overloaded = m.load >= 90;
              return (
                <div
                  key={m.name}
                  className={`flex items-center gap-3 py-2.5 px-3 -mx-3 rounded-lg ${overloaded ? "bg-red-50 border-l-4 border-red-500" : ""}`}
                >
                  {m.userpic ? (
                    <img
                      src={m.userpic}
                      alt={m.name}
                      className="w-8 h-8 rounded-full"
                      style={{ objectFit: "cover" }}
                    />
                  ) : (
                    <Avatar
                      initials={m.init}
                      tone={overloaded ? "alert" : "primary"}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">
                      {m.name}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {m.role}
                    </div>
                  </div>
                  <StatusBadge tone={loadTone}>Load {m.load}%</StatusBadge>
                  <StatusBadge tone="info">Eff {m.eff}%</StatusBadge>
                </div>
              );
            })}
          </div>
          {!teamWorkload.length && (
            <EmptyState
              icon={<Users className="size-5" />}
              label="No team workload data"
            />
          )}
        </Card>

        {/* PROCUREMENT TREND */}
        <Card
          className="col-span-12 xl:col-span-5"
          title="Procurement Trend"
          icon={<TrendingUp className="size-3.5" />}
          action={
            <StatusBadge
              tone={currentPoSpend > previousPoSpend ? "alert" : "good"}
            >
              {percentDiff(currentPoSpend, previousPoSpend)} MoM
            </StatusBadge>
          }
        >
          <div className="grid gap-4 sm:grid-cols-[180px_1fr] items-end min-h-[150px]">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                This Month
              </div>
              <div className="mt-1 text-3xl font-bold tracking-tight font-mono">
                {formatCurrencyShort(
                  currentPoSpend ||
                    safeNumber(procurementOverview?.total_po_value),
                )}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {safeNumber(procurementOverview?.purchase_orders_total)} POs ·{" "}
                {safeNumber(procurementOverview?.mrs_total)} MRs
              </div>
              <div className="mt-5 text-xs text-muted-foreground">
                Highest spend:{" "}
                <span className="font-semibold text-foreground">
                  {topVendor?.vendor_name || "No vendor"} (
                  {formatCurrencyShort(
                    safeNumber(topVendor?.total_purchase_value),
                  )}
                  )
                </span>
              </div>
            </div>
            <div className="h-[132px] min-w-0">
              {procureTrend.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={procureTrend}
                    margin={{ top: 14, right: 10, bottom: 0, left: 10 }}
                  >
                    <defs>
                      <linearGradient
                        id="ownerPoTrend"
                        x1="0"
                        x2="0"
                        y1="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="#3B0069"
                          stopOpacity="0.32"
                        />
                        <stop
                          offset="100%"
                          stopColor="#3B0069"
                          stopOpacity="0"
                        />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="m"
                      axisLine={false}
                      tickLine={false}
                      interval={0}
                      tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                    />
                    <YAxis hide domain={["dataMin - 4", "dataMax + 4"]} />
                    <Tooltip
                      contentStyle={{
                        background: "#ffffff",
                        border: "1px solid #C3C3C3",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="v"
                      stroke="#3B0069"
                      strokeWidth={2.5}
                      fill="url(#ownerPoTrend)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState
                  icon={<TrendingUp className="size-5" />}
                  label="No procurement trend yet"
                />
              )}
            </div>
          </div>
        </Card>

        {/* RECENT MR / PO / RFQ */}
        <Card
          className="col-span-12 xl:col-span-7"
          title="Recent MR / PO / RFQ"
          icon={<ShoppingCart className="size-3.5" />}
        >
          <div className="divide-y divide-border/60">
            {recentProcurement.map((item) => (
              <div
                key={`${item.type}-${item.item}`}
                className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
              >
                <span className="w-10 shrink-0 rounded-full bg-muted px-2 py-1 text-center text-[10px] font-bold text-muted-foreground">
                  {item.type}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">
                    {item.item}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {item.meta}
                  </div>
                </div>
                <div className="shrink-0 text-sm font-bold font-mono">
                  {item.amount}
                </div>
              </div>
            ))}
          </div>
          {!recentProcurement.length && (
            <EmptyState
              icon={<ShoppingCart className="size-5" />}
              label="No recent procurement requests"
            />
          )}
        </Card>

        {/* LIVE ACTIVITY */}
        <Card
          className="col-span-12"
          title="Live Activity"
          icon={<Activity className="size-3.5" />}
          action={
            <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-700 font-semibold">
              <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
              LIVE
            </span>
          }
        >
          <ol className="space-y-0">
            {liveActivities.map((e, i) => {
              const ring =
                e.tone === "emerald"
                  ? "bg-emerald-500"
                  : e.tone === "blue"
                    ? "bg-blue-500"
                    : e.tone === "violet"
                      ? "bg-purple-500"
                      : e.tone === "amber"
                        ? "bg-amber-500"
                        : "bg-red-500";
              const isLast = i === liveActivities.length - 1;
              return (
                <li key={i} className="relative flex gap-3 pb-4">
                  <div className="relative flex w-9 shrink-0 justify-center">
                    {!isLast && (
                      <span
                        className="absolute -bottom-[48px] top-[18px] w-[1.5px] rounded-full bg-slate-300"
                        aria-hidden="true"
                      />
                    )}
                    {e.actorUserpic ? (
                      <img
                        src={e.actorUserpic}
                        alt={e.actorName}
                        className="relative z-[1] size-9 rounded-full border border-border bg-sm-card object-cover ring-4 ring-sm-card"
                      />
                    ) : (
                      <span
                        className={`relative z-[1] flex size-9 items-center justify-center rounded-full text-[11px] font-bold text-white ring-4 ring-sm-card ${ring}`}
                      >
                        {e.actorInitials}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <div className="text-sm font-medium leading-tight text-foreground">
                      {e.text}
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      <span className="font-semibold text-foreground">
                        {e.actorName}
                      </span>{" "}
                      · {e.site} · {e.time} ago
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
          {!liveActivities.length && (
            <EmptyState
              icon={<Activity className="size-5" />}
              label="No live activity yet"
            />
          )}
        </Card>

        {/* RECENT DELIVERIES */}
        <Card
          className="col-span-12 xl:col-span-4"
          title="Recent Deliveries"
          icon={<Truck className="size-3.5" />}
        >
          <div className="divide-y divide-border/60">
            {recentDeliveries.map((delivery) => (
              <div
                key={`${delivery.delivery_id}-${delivery.material}`}
                className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="size-10 rounded-lg border border-border bg-sm-card flex items-center justify-center shrink-0">
                  <Truck className="size-4 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">
                    {delivery.material}
                  </div>
                  <div className="text-xs text-muted-foreground leading-relaxed">
                    {delivery.meta}
                  </div>
                </div>
                <StatusBadge tone={delivery.tone}>
                  {delivery.status}
                </StatusBadge>
              </div>
            ))}
          </div>
          {!recentDeliveries.length && (
            <EmptyState
              icon={<Truck className="size-5" />}
              label="No recent deliveries"
            />
          )}
        </Card>

        {/* DESIGN RECENT PINS */}
        <Card
          className="col-span-12 xl:col-span-4"
          title="Design · Recent Pins"
          icon={<Pin className="size-3.5" />}
          action={
            <StatusBadge tone="warn">{recentPins.length} Open</StatusBadge>
          }
        >
          <div className="divide-y divide-border/60">
            {recentPins.map((pin) => (
              <div
                key={`${pin.design_pin_id}`}
                className={`flex items-center gap-3 py-3 first:pt-0 last:pb-0 ${pin.design_id ? "cursor-pointer rounded-lg px-2 -mx-2 transition-colors hover:bg-muted/40" : ""}`}
                onClick={() => handleDesignPinClick(pin.design_id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleDesignPinClick(pin.design_id);
                  }
                }}
                role={pin.design_id ? "button" : undefined}
                tabIndex={pin.design_id ? 0 : undefined}
              >
                <Pin className="size-4 shrink-0 text-orange-500" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">
                    {pin.title}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {pin.meta}
                  </div>
                </div>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
              </div>
            ))}
          </div>
          {!recentPins.length && (
            <EmptyState
              icon={<Pin className="size-5" />}
              label="No recent design pins"
            />
          )}
        </Card>

        {/* VENDORS OVERVIEW */}
        <Card
          className="col-span-12 xl:col-span-4"
          title="Vendors"
          icon={<Users className="size-3.5" />}
        >
          <div className="divide-y divide-border/60">
            {vendorsOverview.map((vendor) => (
              <div
                key={`${vendor.vendor_id || vendor.name}`}
                className={`flex items-center gap-3 py-3 first:pt-0 last:pb-0 ${vendor.vendor_id ? "cursor-pointer rounded-lg px-2 -mx-2 transition-colors hover:bg-muted/40" : ""}`}
                onClick={() => handleVendorClick(vendor.vendor_id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleVendorClick(vendor.vendor_id);
                  }
                }}
                role={vendor.vendor_id ? "button" : undefined}
                tabIndex={vendor.vendor_id ? 0 : undefined}
              >
                <Avatar
                  initials={vendor.init}
                  tone={vendor.tone === "good" ? "good" : "alert"}
                  size={30}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">
                    {vendor.name}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {vendor.meta}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div
                    className={`text-xs font-bold ${vendor.tone === "good" ? "text-emerald-600" : "text-red-500"}`}
                  >
                    {vendor.due}
                  </div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    {vendor.age}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {!vendorsOverview.length && (
            <EmptyState
              icon={<Users className="size-5" />}
              label="No vendor overview yet"
            />
          )}
        </Card>
      </div>

      {activeApproval?.typeKey === "pettycash" && (
        <PettyCashApprovalDrawer
          approval={activeApproval.raw}
          organizationId={organizationId}
          onClose={() => setActiveApproval(null)}
          onActionComplete={handlePettyCashActionComplete}
        />
      )}

      {activeApproval?.typeKey === "subtasks" && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/30"
            onClick={() => setActiveApproval(null)}
          />
          <SubtaskDetailComponent
            subtask={{
              ...activeApproval.raw,
              id: activeApproval.raw?.subtask_id || activeApproval.raw?.id,
              subtask_name: activeApproval.raw?.subtask_name,
              name: activeApproval.raw?.subtask_name,
              task_id: activeApproval.raw?.task_id,
            }}
            taskId={activeApproval.raw?.task_id}
            onClose={() => setActiveApproval(null)}
            refetchSubtasks={() => {}}
            initialTab="Approvals"
          />
        </>
      )}

      {activeApproval?.typeKey === "procurement" && (
        <MaterialRequest
          Request={normalizeMaterialRequest(activeApproval.raw)}
          onClose={handleMaterialRequestClosed}
          onApprove={() => handleMaterialRequestAction(activeApproval.raw)}
          onReject={() => handleMaterialRequestAction(activeApproval.raw)}
          onProceedToRFQ={() => setActiveApproval(null)}
        />
      )}
    </div>
  );
}

export default function OwnerDashboard(props) {
  return <GlobalOwner {...props} />;
}
